/**
 * `createDocStore` (W1-09 state-store; contract src/state/types.ts, ARCHITECTURE.md §5.3, DESIGN.md §5.13).
 *
 * THE main-thread document: a DocContainer plus undo history, live gestures, coalescing and subscriptions.
 * `transact` runs a core command body in a TxDraft (core/doc/txDraft.ts) over the container; the store adds only:
 *  1. validation of the draft (`Ops.validateTouched`; strict/dev builds also run the full `checkInvariants` and refuse
 *     any NEW violation) -- an invalid transaction is rejected whole, nothing is applied;
 *  2. apply to the container (rolled back if `apply` throws), version-topic bumps (React selectors);
 *  3. a Txn with its HistMarker (`push`, or `merge` into the top entry when coalescing) and selection snapshots
 *     captured through the SelectionBridge (before; after = before + `tx.setSelection`, applied through the bridge);
 *  4. broadcast. One commit happens in this order: the container is applied and the history recorded (history
 *     listeners run as the history changes), the version counters are bumped, the selection is set through the
 *     bridge (editor-store listeners already read the new document and versions), then -- queued, so a listener that
 *     commits again is delivered after the commit in progress -- the ChangeSet to `subscribe` (engine, timeline), the
 *     Txn to `subscribeCommits` (persist and analysis workers), the version-topic listeners (React), and last the
 *     notices the commit caused (`historyDropped`).
 * Undo/redo apply the entry's inverse / forward ops as a NEW forward Txn (`kind` 'undo'/'redo', `hist` undo/redo of
 * the entry's seq), restore the selection of that moment and carry a focus for "jump to change" (explicit, else
 * derived from the ops). `jumpTo` steps one entry at a time, so every step is an ordinary journal entry and the fold
 * over the journal reproduces the history. Replays are checked against the document:
 *  - ops that no longer match it (`ops.stale` from `tx.applyOps`) mean the history is stale: it is cleared with a
 *    journaled `reset` marker and a `historyStale` notice;
 *  - an exact replay restores a state the document already had, so violations that state had are not new. A
 *    strict store records, per entry, the violations of the states before and after it (`ReplayAllowance`) and
 *    refuses only violations outside them and the current baseline (`replayRefused` notice; the history is KEPT).
 *    Entries restored from the journal and non-strict stores trust the exact replay (the journal they come from
 *    was validated commit by commit, and the persist worker re-checks it on open).
 * An entry without net effect (a coalesced run that came back to its start) is undone / redone together with the
 * step it follows, so Ctrl+Z / Ctrl+Y always change something visible; every entry still gets its own marker. The
 * visible step is checked first: when it cannot be replayed (refused, or the history is stale) the steps without
 * effect are not moved past either, and undo / redo return null, so the shell never reports "Undid" for nothing.
 * The Txn returned is the visible step's (the last step replayed only when the run has no visible step at all).
 *
 * `subscribeVersions` / `stamp` (topic-filtered notifications for `useDoc`), the history's version / dropped
 * entries / truncated flag / folded form, `sealHistory`, `clearHistory` and `subscribeNotices` are contract members
 * (CR-W1-09-2); `DocStoreExt` adds only the diagnostics `lastRejection` and `selectionBridge`.
 */

import type { MessageRef, Unsubscribe } from '../core/util/types';
import type { ChartDoc, DocIndexes, InvariantViolation, LineId, LineProp, ProjectId } from '../core/model/types';
import { createDocContainer } from '../core/doc/container';
import { createTxDraft } from '../core/doc/txDraft';
import {
  checkInvariants,
  checkInvariantsCached,
  createInvariantCache,
  type InvariantCache,
} from '../core/model/invariants';
import { LINE_PROPS } from '../core/model/props';
import { Ops } from '../core/ops/ops';
import type {
  ChangeSet,
  ChangeSource,
  DirtyScopes,
  DocContainer,
  HistMarker,
  Op,
  SelectionSnapshot,
  Txn,
  TxBuilder,
  TxDraft,
  TxnFocus,
} from '../core/ops/types';
import { canCoalesce } from './coalesce';
import { Emitter } from './emitter';
import { deriveFocus } from './focus';
import { type AppliedBatch, type GestureHost, LiveGesture } from './gesture';
import { hasRecordOps, UndoHistory, type HistoryItem, type ReplayAllowance } from './history';
import { EMPTY_SNAPSHOT } from './selection';
import { m } from './state.messages';
import type {
  CreateDocStore,
  CreateDocStoreOptions,
  DocStore,
  DocStoreNotice,
  Gesture,
  HistoryView,
  SelectionBridge,
  TxnMeta,
  TxnOutcome,
  VersionTopic,
} from './types';
import { topicsOf, VersionCounters } from './versions';

// ---------------------------------------------------------------------------------------------------------------
// Public additions
// ---------------------------------------------------------------------------------------------------------------

/** TxnMeta plus the transaction kind for imports merged into the project and version restores. */
/** Load options (the contract's `DocStore.load` options). */
export type LoadOptions = Parameters<DocStore['load']>[1];

export type { DocStoreNotice } from './types';

export interface DocStoreRejection {
  readonly label: MessageRef;
  readonly command?: string;
  readonly violations: readonly InvariantViolation[];
}

/**
 * The DocStore with W1-09's diagnostics beyond the contract (the former DocStoreExt additions are contract members
 * since CR-W1-09-2).
 */
export interface DocStoreExt extends DocStore {
  /** The last transaction refused for invariant violations (diagnostics), or null. */
  readonly lastRejection: DocStoreRejection | null;
  /** The current selection bridge. */
  readonly selectionBridge: SelectionBridge;
}

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

/** Bridge used until the editor connects one: empty selection, restores ignored. */
export const DETACHED_BRIDGE: SelectionBridge = Object.freeze({
  get: () => EMPTY_SNAPSHOT,
  set: () => undefined,
});

function violationKey(v: InvariantViolation): string {
  return `${v.code}|${v.table ?? ''}|${String(v.id ?? '')}`;
}

/** The user-facing reason of a refused transaction: the first violation's own message (CR-W1-03-2), else a count. */
export function rejectionReason(violations: readonly InvariantViolation[]): MessageRef {
  const first = violations[0];
  if (first !== undefined && first.code !== '' && first.params !== undefined)
    return { key: first.code, params: first.params };
  return { key: m.txnRejected, params: { count: violations.length } };
}

/** Every scope dirty (document replaced). */
export function fullDirty(doc: ChartDoc): DirtyScopes {
  const lines = [...doc.lines.keys()];
  const motion = new Map<LineId, ReadonlySet<LineProp>>();
  const all = new Set<LineProp>(LINE_PROPS);
  for (const id of lines) motion.set(id, all);
  return {
    tempo: true,
    timing: true,
    meta: true,
    pkg: true,
    editor: true,
    rpeRoot: true,
    lineSet: true,
    lineProps: new Set(lines),
    lineMotion: motion,
    lineNotes: new Set(lines),
    effects: true,
    markers: true,
    groups: true,
    assets: true,
    generators: true,
  };
}

function withOptional(
  base: Omit<Txn, 'coalesceKey' | 'focus' | 'command'>,
  opt: { coalesceKey?: string | undefined; focus?: TxnFocus | undefined; command?: string | undefined },
): Txn {
  return {
    ...base,
    ...(opt.coalesceKey !== undefined ? { coalesceKey: opt.coalesceKey } : {}),
    ...(opt.focus !== undefined ? { focus: opt.focus } : {}),
    ...(opt.command !== undefined ? { command: opt.command } : {}),
  };
}

/** A replay that can be applied: its ops and (strict stores) the full invariant check of the state it leads to. */
interface ReplayOk {
  readonly k: 'ok';
  readonly ops: readonly Op[];
  readonly selBefore: SelectionSnapshot;
  readonly checked: readonly InvariantViolation[] | null;
}

/** The outcome of running a replay in a draft (nothing applied yet). */
type ReplayPlan =
  | ReplayOk
  /** The ops do not match the document (`ops.stale`). */
  | { readonly k: 'stale' }
  /** Strict store: the replay would add NEW invariant violations. */
  | { readonly k: 'refused'; readonly violations: readonly InvariantViolation[] };

/** A recorded transaction and the notice its recording caused (delivered after the commit). */
interface Recorded {
  readonly txn: Txn;
  readonly notice: DocStoreNotice | null;
}

/** Dev builds (and tests) validate every invariant after every commit unless told otherwise. */
function strictDefault(): boolean {
  return import.meta.env.DEV;
}

/** Full `checkInvariants` at least this often (commits), else `checkInvariantsCached` (CR-W3-03-1, UX-029). */
const FULL_CHECK_EVERY = 256;
/** Below this many records the full check runs on every commit (tests and small charts stay exact). */
const FULL_CHECK_RECORDS = 2000;

/** Records in every table (the size test of the full-check cadence above). */
function recordCount(doc: ChartDoc): number {
  return (
    doc.bpm.size +
    doc.lines.size +
    doc.tracks.size +
    doc.keys.size +
    doc.notes.size +
    doc.effects.size +
    doc.videos.size +
    doc.markers.size +
    doc.groups.size +
    doc.generators.size +
    doc.assets.size
  );
}

// ---------------------------------------------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------------------------------------------

class DocStoreImpl implements DocStoreExt {
  readonly projectId: ProjectId;
  private container: DocContainer;
  private seqNo: number;
  private readonly strict: boolean;
  private readonly now: () => number;
  private readonly hist: UndoHistory;
  private readonly versions = new VersionCounters();
  private readonly changes = new Emitter<ChangeSet>();
  private readonly commits = new Emitter<Txn>();
  private readonly notices = new Emitter<DocStoreNotice>();
  private bridge: SelectionBridge = DETACHED_BRIDGE;
  private gesture: LiveGesture | null = null;
  private isBuilding = false;
  /** Strict mode: violations already present (only NEW ones refuse a transaction). */
  private baseline: Set<string> | null = null;
  /** Full-check result of the last accepted draft (strict), adopted as the baseline once the commit is applied. */
  private pendingBaseline: readonly InvariantViolation[] | null = null;
  /** Shape cache of `checkInvariantsCached` (CR-W3-03-1, UX-029); reset with the baseline. */
  private invCache: InvariantCache = createInvariantCache();
  /** Commits since the last full `checkInvariants` (the cached check is what every commit runs). */
  private sinceFullCheck = 0;
  private rejection: DocStoreRejection | null = null;
  private readonly outbox: (() => void)[] = [];
  private flushing = false;
  private readonly host: GestureHost;

  constructor(opts: CreateDocStoreOptions) {
    this.projectId = opts.projectId;
    this.container = createDocContainer(opts.doc);
    this.seqNo = opts.seq;
    this.strict = opts.strict ?? strictDefault();
    this.now = opts.now ?? Date.now;
    this.hist = new UndoHistory(opts.undoMemoryCapBytes);
    if (opts.history !== undefined && opts.history.length > 0)
      this.hist.load(opts.history, opts.historyPosition, opts.historyTruncated ?? false);
    else if (opts.historyTruncated === true) this.hist.reset(true);
    this.resetBaseline();
    this.host = this.makeHost();
  }

  // ----- EngineSource / reads -----

  get doc(): ChartDoc {
    return this.container.doc;
  }
  get idx(): DocIndexes {
    return this.container.idx;
  }
  get seq(): number {
    return this.seqNo;
  }
  get history(): HistoryView {
    return this.hist;
  }
  get gestureActive(): boolean {
    return this.gesture !== null;
  }
  get lastRejection(): DocStoreRejection | null {
    return this.rejection;
  }
  get selectionBridge(): SelectionBridge {
    return this.bridge;
  }

  version(topic: VersionTopic): number {
    return this.versions.version(topic);
  }
  stamp(topics: readonly VersionTopic[]): number {
    return this.versions.stamp(topics);
  }

  subscribe(cb: (cs: ChangeSet) => void): Unsubscribe {
    return this.changes.subscribe(cb);
  }
  subscribeCommits(cb: (txn: Txn) => void): Unsubscribe {
    return this.commits.subscribe(cb);
  }
  subscribeVersions(topics: readonly VersionTopic[] | null, cb: () => void): Unsubscribe {
    return this.versions.subscribe(topics, cb);
  }
  subscribeNotices(cb: (n: DocStoreNotice) => void): Unsubscribe {
    return this.notices.subscribe(cb);
  }

  setSelectionBridge(b: SelectionBridge): void {
    this.bridge = b;
  }

  // ----- transactions -----

  transact<R>(meta: TxnMeta, build: (tx: TxBuilder) => R): TxnOutcome<R> {
    this.assertNotBuilding('transact');
    if (this.gesture !== null) return { ok: false, reason: { key: m.gestureActive } };
    const selBefore = this.bridge.get();
    const draft = createTxDraft(this.container, selBefore);
    const r = this.building(() => draft.run(build));
    if (!r.ok) return { ok: false, reason: r.reason };
    const res = draft.finish();
    if (!hasRecordOps(res.ops)) {
      if (res.selection !== null) this.applySelection({ ...selBefore, ...res.selection });
      return { ok: true, value: r.value, txn: null, notes: res.notes };
    }
    const violations = this.violationsOf(draft, res.ops);
    if (violations.length > 0) {
      this.reject(meta, violations);
      return { ok: false, reason: rejectionReason(violations) };
    }
    let batch: AppliedBatch;
    try {
      batch = this.applySilently(res.ops);
    } catch (e) {
      this.pendingBaseline = null;
      throw e;
    }
    const selAfter = res.selection === null ? selBefore : { ...selBefore, ...res.selection };
    const { txn, notice } = this.record(meta, res.ops, selBefore, selAfter, res.focus ?? meta.focus);
    this.announce('txn', txn, [batch], res.selection === null ? null : selAfter, notice);
    return { ok: true, value: r.value, txn, notes: res.notes };
  }

  beginGesture(meta: TxnMeta): Gesture {
    this.assertNotBuilding('beginGesture');
    // A gesture left open by its caller would block every transaction: it is cancelled (no half-applied state).
    this.gesture?.cancel();
    const g = new LiveGesture(this.host, meta, this.bridge.get());
    this.gesture = g;
    return g;
  }

  // ----- undo / redo -----

  undo(): Txn | null {
    this.assertNotBuilding('undo');
    if (this.gesture !== null) {
      // Ctrl+Z during a drag reverts the drag.
      this.gesture.cancel();
      return null;
    }
    // Steps without net effect on top, then the visible step they follow.
    const run: HistoryItem[] = [];
    for (let i = this.hist.position; i >= 0; i--) {
      const entry = this.hist.item(i);
      if (entry === undefined) break;
      run.push(entry);
      if (hasRecordOps(entry.ops)) return this.replayRun(run, 'undo', run.length - 1);
    }
    return this.replayRun(run, 'undo', -1);
  }

  redo(): Txn | null {
    this.assertNotBuilding('redo');
    if (this.gesture !== null) {
      this.gesture.cancel();
      return null;
    }
    // Leading steps without net effect, the next visible step, then the steps without effect that follow it.
    const run: HistoryItem[] = [];
    let visible = -1;
    for (let i = this.hist.position + 1; i < this.hist.length; i++) {
      const entry = this.hist.item(i);
      if (entry === undefined) break;
      const effect = hasRecordOps(entry.ops);
      if (effect && visible >= 0) break;
      if (effect) visible = run.length;
      run.push(entry);
    }
    return this.replayRun(run, 'redo', visible);
  }

  jumpTo(position: number): void {
    this.assertNotBuilding('jumpTo');
    this.gesture?.cancel();
    if (!Number.isFinite(position)) return;
    const target = Math.max(-1, Math.min(this.hist.length - 1, Math.trunc(position)));
    while (this.hist.position > target) {
      const entry = this.hist.current();
      if (entry === undefined || this.replay(entry, 'undo') === null) return;
    }
    while (this.hist.position < target) {
      const entry = this.hist.next();
      if (entry === undefined || this.replay(entry, 'redo') === null) return;
    }
  }

  sealHistory(): void {
    this.hist.seal();
  }

  clearHistory(): void {
    this.assertNotBuilding('clearHistory');
    this.writeReset(false);
  }

  // ----- load -----

  load(doc: ChartDoc, opts: LoadOptions): void {
    this.assertNotBuilding('load');
    const g = this.gesture;
    this.gesture = null;
    g?.detach();
    this.container = createDocContainer(doc);
    this.seqNo = opts.seq;
    if (opts.history !== undefined && opts.history.length > 0)
      this.hist.load(opts.history, opts.historyPosition, opts.historyTruncated ?? false);
    else this.hist.reset(opts.historyTruncated ?? false);
    this.rejection = null;
    this.resetBaseline();
    const due = this.versions.bumpAll();
    const cs: ChangeSet = { source: 'load', ops: [], dirty: fullDirty(this.container.doc) };
    this.deliver(() => {
      this.changes.emit(cs);
      VersionCounters.notify(due);
    });
  }

  // ----- internals -----

  private assertNotBuilding(what: string): void {
    if (this.isBuilding) throw new Error(`DocStore.${what}: called inside a transaction build function`);
  }

  private building<T>(fn: () => T): T {
    this.assertNotBuilding('transact');
    this.isBuilding = true;
    try {
      return fn();
    } finally {
      this.isBuilding = false;
    }
  }

  private resetBaseline(): void {
    this.invCache = createInvariantCache();
    this.sinceFullCheck = 0;
    this.baseline = this.strict
      ? new Set(checkInvariants(this.container.doc, this.container.idx).map(violationKey))
      : null;
  }

  /**
   * CR-W3-03-1 (UX-029): the per-commit FULL `checkInvariants` is the edit latency on large charts (570-1230 ms
   * dev edits on the 30k chart). The full check still runs on every commit below `FULL_CHECK_RECORDS` records
   * (tests and small charts stay exact) and every `FULL_CHECK_EVERY`-th commit otherwise; in between the same
   * check runs with the shape cache (`checkInvariantsCached`), which skips re-validating records already seen
   * valid (records are immutable, so only changed ones are walked).
   */
  private violationsOf(draft: TxDraft, ops: readonly Op[]): readonly InvariantViolation[] {
    const touched = Ops.validateTouched(draft.tx.doc, draft.tx.idx, ops);
    if (touched.length > 0 || this.baseline === null) return touched;
    const baseline = this.baseline;
    const full = this.sinceFullCheck + 1 >= FULL_CHECK_EVERY || recordCount(draft.tx.doc) < FULL_CHECK_RECORDS;
    this.sinceFullCheck = full ? 0 : this.sinceFullCheck + 1;
    const all = full
      ? checkInvariants(draft.tx.doc, draft.tx.idx)
      : checkInvariantsCached(draft.tx.doc, draft.tx.idx, this.invCache);
    const fresh = all.filter((v) => !baseline.has(violationKey(v)));
    if (fresh.length === 0) this.pendingBaseline = all;
    return fresh;
  }

  /** Makes the full-check result of the accepted draft the baseline (the same Set is kept while it is unchanged). */
  private adoptBaseline(): void {
    const current = this.baseline;
    const pending = this.pendingBaseline;
    this.pendingBaseline = null;
    if (current === null || pending === null) return;
    const next = new Set(pending.map(violationKey));
    if (next.size !== current.size || [...next].some((k) => !current.has(k))) this.baseline = next;
  }

  /**
   * Strict replay check: the full invariant check of the replayed state (`all`, the next baseline once applied) and
   * its NEW violations (`fresh`): those neither the current document has nor the restored state had when the entry
   * was recorded. Null for non-strict stores; entries restored from the journal trust the exact replay (no `fresh`;
   * see the file header).
   */
  private replayCheck(
    draft: TxDraft,
    entry: HistoryItem,
    dir: 'undo' | 'redo',
  ): { readonly fresh: readonly InvariantViolation[]; readonly all: readonly InvariantViolation[] } | null {
    const baseline = this.baseline;
    if (baseline === null) return null;
    const all = checkInvariants(draft.tx.doc, draft.tx.idx);
    const allow = entry.allow === null ? null : dir === 'undo' ? entry.allow.undo : entry.allow.redo;
    const fresh =
      allow === null
        ? []
        : all.filter((v) => {
            const k = violationKey(v);
            return !baseline.has(k) && !allow.has(k);
          });
    return { fresh, all };
  }

  private reject(meta: TxnMeta, violations: readonly InvariantViolation[]): void {
    this.pendingBaseline = null;
    this.rejection = {
      label: meta.label,
      ...(meta.command !== undefined ? { command: meta.command } : {}),
      violations,
    };
  }

  /** Applies ops to the container (rolled back when apply throws); dirty scopes and topics from the state before. */
  private applySilently(ops: readonly Op[]): AppliedBatch {
    const before = this.container.doc;
    const dirty = Ops.dirtyOf(ops, before);
    const topics = topicsOf(ops, dirty, before);
    let i = 0;
    try {
      for (; i < ops.length; i++) this.container.apply(ops[i] as Op);
    } catch (e) {
      this.container.applyAll(Ops.invertAll(ops.slice(0, i)));
      throw e;
    }
    return { ops, dirty, topics };
  }

  /**
   * Bumps the topics of `batches`, sets `selection` through the bridge (when given) and queues the ChangeSet, the
   * commit, the version listeners and `notice` for delivery, in that order (see the file header).
   */
  private announce(
    source: ChangeSource,
    txn: Txn | null,
    batches: readonly AppliedBatch[],
    selection: SelectionSnapshot | null = null,
    notice: DocStoreNotice | null = null,
  ): void {
    const topics = new Set<VersionTopic>();
    let dirty = Ops.emptyDirty();
    const raw: Op[] = [];
    for (const b of batches) {
      for (const t of b.topics) topics.add(t);
      dirty = Ops.mergeDirty(dirty, b.dirty);
      raw.push(...b.ops);
    }
    // Live changes are announced as their net effect (revert + reapply composed); no net effect, no announcement.
    const ops = txn !== null ? txn.ops : batches.length > 1 ? Ops.compose(raw) : raw;
    if (txn === null && ops.length === 0) {
      if (selection !== null) this.applySelection(selection);
      this.emitNotice(notice);
      return;
    }
    const due = this.versions.bump(topics);
    if (selection !== null) this.applySelection(selection);
    const cs: ChangeSet = txn !== null ? { source, seq: txn.seq, ops, dirty } : { source, ops, dirty };
    this.deliver(() => {
      this.changes.emit(cs);
      if (txn !== null) this.commits.emit(txn);
      VersionCounters.notify(due);
      if (notice !== null) this.notices.emit(notice);
    });
  }

  /** Queues a notice behind everything already queued. */
  private emitNotice(notice: DocStoreNotice | null): void {
    if (notice === null) return;
    this.deliver(() => {
      this.notices.emit(notice);
    });
  }

  /** Delivers notifications in commit order, also when a listener commits again. */
  private deliver(fn: () => void): void {
    this.outbox.push(fn);
    if (this.flushing) return;
    this.flushing = true;
    try {
      for (let next = this.outbox.shift(); next !== undefined; next = this.outbox.shift()) next();
    } finally {
      this.flushing = false;
    }
  }

  private applySelection(sel: SelectionSnapshot): void {
    this.bridge.set(sel);
  }

  /**
   * Records a committed `do` transaction (already applied) in the history. Returns it with the `historyDropped`
   * notice when the memory cap dropped entries; the caller delivers the notice after the commit.
   */
  private record(
    meta: TxnMeta,
    ops: readonly Op[],
    selBefore: SelectionSnapshot,
    selAfter: SelectionSnapshot,
    focus: TxnFocus | null | undefined,
  ): Recorded {
    // Strict stores remember the violations of the states before and after the entry (undo/redo may restore them).
    const before = this.baseline;
    this.adoptBaseline();
    const after = this.baseline;
    const allow: ReplayAllowance | null = before !== null && after !== null ? { undo: before, redo: after } : null;
    const at = this.now();
    const prevSeq = this.seqNo;
    const seq = prevSeq + 1;
    this.seqNo = seq;
    const top = this.hist.current() ?? null;
    const merge = canCoalesce({ key: meta.coalesceKey, now: at, top, atTop: this.hist.atTop(), lastSeq: prevSeq });
    const hist: HistMarker = merge && top !== null ? { k: 'merge', into: top.seq } : { k: 'push' };
    const txn = withOptional(
      { seq, kind: meta.kind ?? 'do', label: meta.label, ops, at, selBefore, selAfter, hist },
      { coalesceKey: meta.coalesceKey, focus: focus ?? undefined, command: meta.command },
    );
    const dropped = hist.k === 'merge' ? this.hist.merge(txn, allow) : this.hist.push(txn, allow);
    const notice: DocStoreNotice | null =
      dropped > 0
        ? { kind: 'historyDropped', count: dropped, message: { key: m.historyDropped, params: { count: dropped } } }
        : null;
    return { txn, notice };
  }

  /**
   * Replays `run` (consecutive entries in replay order). `visible` is the index of the step with an effect, -1 when
   * the run has none. Steps without effect before the visible step are replayed only once the visible step is known
   * to apply (a failed check is reported as usual). Returns the visible step's Txn, null when it was not replayed,
   * or, for a run without a visible step, the last Txn replayed.
   */
  private replayRun(run: readonly HistoryItem[], dir: 'undo' | 'redo', visible: number): Txn | null {
    const target = run[visible];
    if (visible > 0 && target !== undefined && this.viable(this.planReplay(target, dir), target, dir) === null)
      return null;
    let result: Txn | null = null;
    for (let i = 0; i < run.length; i++) {
      const entry = run[i];
      if (entry === undefined) break;
      const txn = this.replay(entry, dir);
      // Before (or at) the visible step nothing visible was replayed; after it, the visible step stands.
      if (txn === null) return i <= visible ? null : result;
      if (i === visible || visible < 0) result = txn;
    }
    return result;
  }

  /** Undo (inverse ops) or redo (forward ops) of a history entry as a new forward transaction. */
  private replay(entry: HistoryItem, dir: 'undo' | 'redo'): Txn | null {
    const plan = this.viable(this.planReplay(entry, dir), entry, dir);
    return plan === null ? null : this.commitReplay(entry, dir, plan);
  }

  /**
   * Runs the replay of `entry` in a draft over the container and checks it (strict stores); nothing is applied and
   * the baseline is not touched, so a plan can also serve as a pure check.
   */
  private planReplay(entry: HistoryItem, dir: 'undo' | 'redo'): ReplayPlan {
    const ops = dir === 'undo' ? Ops.invertAll(entry.ops) : entry.ops;
    const selBefore = this.bridge.get();
    const draft = createTxDraft(this.container, selBefore);
    const r = this.building(() =>
      draft.run((tx) => {
        tx.applyOps(ops);
      }),
    );
    if (!r.ok) return { k: 'stale' };
    const res = draft.finish();
    const check = hasRecordOps(res.ops) ? this.replayCheck(draft, entry, dir) : null;
    if (check !== null && check.fresh.length > 0) return { k: 'refused', violations: check.fresh };
    return { k: 'ok', ops: res.ops, selBefore, checked: check?.all ?? null };
  }

  /** The plan when it can be applied; otherwise the failure is reported (stale: history cleared; refused) and null. */
  private viable(plan: ReplayPlan, entry: HistoryItem, dir: 'undo' | 'redo'): ReplayOk | null {
    switch (plan.k) {
      case 'ok':
        return plan;
      case 'stale':
        this.staleHistory();
        return null;
      case 'refused':
        this.refuseReplay(entry, dir, plan.violations);
        return null;
    }
  }

  /** Applies a viable replay plan and records it as the undo / redo of `entry`. */
  private commitReplay(entry: HistoryItem, dir: 'undo' | 'redo', plan: ReplayOk): Txn {
    const batch = this.applySilently(plan.ops);
    this.pendingBaseline = plan.checked;
    this.adoptBaseline();
    const seq = this.seqNo + 1;
    this.seqNo = seq;
    const selAfter = dir === 'undo' ? entry.selBefore : entry.selAfter;
    const focus = entry.focus ?? deriveFocus(plan.ops, this.container.doc) ?? undefined;
    const txn = withOptional(
      {
        seq,
        kind: dir,
        label: entry.label,
        ops: plan.ops,
        at: this.now(),
        selBefore: plan.selBefore,
        selAfter,
        hist: dir === 'undo' ? { k: 'undo', of: entry.seq } : { k: 'redo', of: entry.seq },
      },
      { focus, command: entry.command },
    );
    if (dir === 'undo') this.hist.stepBack();
    else this.hist.stepForward();
    this.announce(dir, txn, [batch], selAfter);
    return txn;
  }

  /** A replay would add a NEW violation: nothing is applied, the history stays as it is, the user is told why. */
  private refuseReplay(entry: HistoryItem, dir: 'undo' | 'redo', violations: readonly InvariantViolation[]): void {
    this.reject({ label: entry.label, ...(entry.command !== undefined ? { command: entry.command } : {}) }, violations);
    const notice: DocStoreNotice = {
      kind: 'replayRefused',
      dir,
      message: { key: dir === 'undo' ? m.undoRefused : m.redoRefused },
      reason: rejectionReason(violations),
    };
    this.deliver(() => {
      this.notices.emit(notice);
    });
  }

  /** The history no longer matches the document: clear it (journaled) and tell the user. */
  private staleHistory(): void {
    this.writeReset(true);
    const notice: DocStoreNotice = { kind: 'historyStale', message: { key: m.historyStale } };
    this.deliver(() => {
      this.notices.emit(notice);
    });
  }

  /** Clears the history and journals a `reset` marker (an empty transaction) so the persisted fold agrees. */
  private writeReset(truncated: boolean): void {
    if (this.hist.length === 0) {
      if (truncated) this.hist.reset(true);
      return;
    }
    const sel = this.bridge.get();
    const seq = this.seqNo + 1;
    this.seqNo = seq;
    const txn: Txn = {
      seq,
      kind: 'do',
      label: { key: m.historyCleared },
      ops: [],
      at: this.now(),
      selBefore: sel,
      selAfter: sel,
      hist: { k: 'reset' },
    };
    this.hist.reset(truncated);
    this.deliver(() => {
      this.commits.emit(txn);
    });
  }

  /** The container's document with `ops` applied in a draft overlay (the container itself is unchanged). */
  private overlay(ops: readonly Op[]): { readonly doc: ChartDoc; readonly idx: DocIndexes } {
    const draft = createTxDraft(this.container, EMPTY_SNAPSHOT);
    const r = draft.run((tx) => {
      tx.applyOps(ops);
    });
    if (!r.ok) throw new Error(`DocStore: overlay ops do not apply (${r.reason.key})`);
    return draft.tx;
  }

  private makeHost(): GestureHost {
    return {
      draft: (sel) => createTxDraft(this.container, sel),
      building: (fn) => this.building(fn),
      check: (draft, ops) => {
        if (!hasRecordOps(ops)) return null;
        const v = Ops.validateTouched(draft.tx.doc, draft.tx.idx, ops);
        return v.length > 0 ? rejectionReason(v) : null;
      },
      applySilently: (ops) => this.applySilently(ops),
      publish: (source, batches) => {
        this.announce(source, null, batches);
      },
      commitApplied: (meta, ops, selBefore, selection, focus, revert) =>
        this.commitGesture(meta, ops, selBefore, selection, focus, revert),
      applySelection: (sel) => {
        this.applySelection(sel);
      },
      release: (g) => {
        if (this.gesture === g) this.gesture = null;
      },
    };
  }

  private commitGesture(
    meta: TxnMeta,
    ops: readonly Op[],
    selBefore: SelectionSnapshot,
    selection: Partial<SelectionSnapshot> | null,
    focus: TxnFocus | undefined,
    revert: () => void,
  ): Txn | null {
    // Record ops are live already; only the id counter is still to be applied, and only once the commit is
    // accepted (a refused commit must not leave the counter ahead of every replica and the journal).
    const idOps = ops.filter((op) => op.o === 'idSeq');
    if (this.baseline !== null) {
      const baseline = this.baseline;
      // The strict check sees the counter raised through a draft overlay; the container is not touched.
      const view = idOps.length > 0 ? this.overlay(idOps) : this.container;
      const all = checkInvariants(view.doc, view.idx);
      const fresh = all.filter((v) => !baseline.has(violationKey(v)));
      if (fresh.length > 0) {
        this.reject(meta, fresh);
        revert();
        return null;
      }
      this.pendingBaseline = all;
    }
    for (const op of idOps) this.container.apply(op);
    const selAfter = selection === null ? selBefore : { ...selBefore, ...selection };
    const { txn, notice } = this.record(meta, ops, selBefore, selAfter, focus);
    if (selection !== null) this.applySelection(selAfter);
    // The ChangeSets were sent live (versions bumped then); workers and the journal get the commit only now.
    this.deliver(() => {
      this.commits.emit(txn);
      if (notice !== null) this.notices.emit(notice);
    });
    return txn;
  }
}

/** Creates the store, typed with its diagnostics (`DocStoreExt`). */
export function createDocStoreExt(opts: CreateDocStoreOptions): DocStoreExt {
  return new DocStoreImpl(opts);
}

export const createDocStore: CreateDocStore = (opts) => createDocStoreExt(opts);

/**
 * True when `store` has the version subscriptions of the contract. Every real DocStore has them; `useDoc` keeps a
 * fallback for minimal test fakes that implement only `version` / `subscribe`.
 */
export function hasVersionSubscriptions(store: DocStore): boolean {
  return (
    typeof (store as Partial<DocStore>).subscribeVersions === 'function' &&
    typeof (store as Partial<DocStore>).stamp === 'function'
  );
}
